import { useMemo } from 'react'

import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { GeneratorModel } from '../../client/ders'
import useManagedSites from './sites'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type GeneratorFilters = {
  orgId?: string
  siteId?: string
  nodeId?: string
}

type UseManagedGeneratorsInput = {
  filters?: GeneratorFilters
}

export default function useManagedGenerators({ filters }: UseManagedGeneratorsInput = {}) {
  const { generators } = useDataManager()
  const { data, isLoading, isFetching } = generators

  // The manager fetches with `orgIds: [principalOrgId]` + a deep orgDepth,
  // so the cached set is already scoped to the principal user's org tree.
  // The site-join only does real narrowing when the caller wants a sub-slice
  // — i.e. while cloaking, or when an explicit `orgId` filter is passed.
  // Skipping the join (and its loading wait) on the common no-narrowing path
  // keeps the page snappy without sacrificing correctness when narrowing is
  // actually needed.
  const isCloaking = useIsCloaking()
  const needsSiteJoin = isCloaking || filters?.orgId != null

  const { sites: orgSites, isLoading: isSitesLoading, isFetching: isSitesFetching } = useManagedSites({
    filters: { orgId: filters?.orgId }
  })

  return {
    generators: useFilteredGenerators(data, orgSites, needsSiteJoin, filters),
    isLoading: isLoading || (needsSiteJoin && isSitesLoading),
    isFetching: isFetching || (needsSiteJoin && isSitesFetching)
  }
}

function useFilteredGenerators(
  data: GeneratorModel[],
  orgSites: ReturnType<typeof useManagedSites>['sites'],
  needsSiteJoin: boolean,
  filters?: GeneratorFilters
) {
  const currentOrgId = useCurrentOrgId()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  const allowedSiteIds = useMemo(() => new Set(orgSites.map((site) => site.depotId)), [orgSites])

  return useMemo(() => {
    const { siteId, nodeId } = normalizedFilters

    if (siteId === EMPTY || nodeId === EMPTY) {
      return []
    }

    let filtered = needsSiteJoin
      ? data.filter((generator) => allowedSiteIds.has(generator.siteId))
      : data

    if (siteId != null) {
      filtered = filtered.filter((generator) => generator.siteId === siteId)
    }

    if (nodeId != null) {
      filtered = filtered.filter((generator) => generator.nodeId === nodeId)
    }

    return filtered
  }, [data, allowedSiteIds, needsSiteJoin, normalizedFilters])
}
